import CryptoJS from 'crypto-js';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

// Konstante za sigurnosne postavke
const SALT_KEY = '@secure_storage_salt';
const IV_LENGTH = 16; // 16 bajtova za inicijalizacijski vektor

/**
 * Generira hardverski fingerprint za uređaj
 * Koristi dostupne informacije o platformi
 */
const generateDeviceFingerprint = async (): Promise<string> => {
  // Koristimo informacije o platformi koje su dostupne bez dodatnih biblioteka
  const platformInfo = {
    os: Platform.OS,
    version: Platform.Version,
    constants: Platform.constants || {},
  };

  // Dodajemo nasumični identifikator instalacije ako ne postoji
  let installationId = await AsyncStorage.getItem('@installation_id');
  if (!installationId) {
    installationId = CryptoJS.lib.WordArray.random(16).toString();
    await AsyncStorage.setItem('@installation_id', installationId);
  }

  // Stvaramo fingerprint kombiniranjem platformskih informacija i instalacijskog ID-a
  const fingerprintData = JSON.stringify({
    ...platformInfo,
    installationId,
  });

  return CryptoJS.SHA256(fingerprintData).toString();
};

/**
 * Generira ili dohvaća salt za derivaciju ključa
 */
const getSalt = async (): Promise<string> => {
  let salt = await AsyncStorage.getItem(SALT_KEY);
  if (!salt) {
    // Generiramo novi salt ako ne postoji
    salt = CryptoJS.lib.WordArray.random(16).toString();
    await AsyncStorage.setItem(SALT_KEY, salt);
  }
  return salt;
};

/**
 * Generira ključ za kriptiranje podataka
 */
const getEncryptionKey = async (): Promise<string> => {
  const deviceFingerprint = await generateDeviceFingerprint();
  const salt = await getSalt();
  
  // Deriviramo ključ za kriptiranje iz device fingerprinta i salta
  return CryptoJS.PBKDF2(
    deviceFingerprint,
    salt,
    { keySize: 256/32, iterations: 10000 }
  ).toString();
};

/**
 * Kriptira podatke s naprednim sigurnosnim mehanizmima
 * @param value Vrijednost za kriptiranje
 * @returns Kriptirani string s IV i HMAC
 */
export const encryptData = async (value: string): Promise<string> => {
  // Generiramo ključ za kriptiranje
  const encryptionKey = await getEncryptionKey();
  
  // Generiramo nasumični inicijalizacijski vektor
  const iv = CryptoJS.lib.WordArray.random(IV_LENGTH);
  
  // Kriptiramo podatke
  const encrypted = CryptoJS.AES.encrypt(value, encryptionKey, {
    iv: iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7
  });
  
  // Stvaramo HMAC za provjeru integriteta
  const hmac = CryptoJS.HmacSHA256(
    iv.toString() + encrypted.toString(),
    encryptionKey
  ).toString();
  
  // Kombiniramo IV, kriptirane podatke i HMAC
  const result = {
    iv: iv.toString(),
    data: encrypted.toString(),
    hmac: hmac
  };
  
  return JSON.stringify(result);
};

/**
 * Dekriptira podatke
 * @param encryptedValue Kriptirani string
 * @returns Dekriptirana vrijednost ili null ako je došlo do greške
 */
export const decryptData = async (encryptedValue: string | null): Promise<string | null> => {
  if (!encryptedValue) return null;
  
  try {
    // Parsiramo kriptirani objekt
    const encryptedObj = JSON.parse(encryptedValue);
    const { iv, data, hmac } = encryptedObj;
    
    // Generiramo ključ za dekriptiranje
    const decryptionKey = await getEncryptionKey();
    
    // Provjeravamo integritet podataka
    const calculatedHmac = CryptoJS.HmacSHA256(
      iv + data,
      decryptionKey
    ).toString();
    
    // Ako se HMAC ne podudara, podaci su možda bili izmijenjeni
    if (calculatedHmac !== hmac) {
      console.error('Integritet podataka narušen! Mogući pokušaj manipulacije.');
      return null;
    }
    
    // Dekriptiramo podatke
    const bytes = CryptoJS.AES.decrypt(data, decryptionKey, {
      iv: CryptoJS.enc.Hex.parse(iv),
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error('Greška pri dekriptiranju podataka:', error);
    return null;
  }
};

/**
 * Sigurno sprema podatke u AsyncStorage s kriptiranjem
 * @param key Ključ za AsyncStorage
 * @param value Vrijednost za spremanje
 */
export const secureSetItem = async (key: string, value: string): Promise<void> => {
  const encryptedValue = await encryptData(value);
  await AsyncStorage.setItem(key, encryptedValue);
};

/**
 * Sigurno dohvaća podatke iz AsyncStorage s dekriptiranjem
 * @param key Ključ za AsyncStorage
 * @returns Dekriptirana vrijednost ili null
 */
export const secureGetItem = async (key: string): Promise<string | null> => {
  const encryptedValue = await AsyncStorage.getItem(key);
  return decryptData(encryptedValue);
};
